import React, { useState, useRef, useEffect } from 'react';

interface DateRangeFilterProps {
  label: string;
  /** YYYY-MM-DD start, empty string = no lower bound */
  fromValue: string;
  /** YYYY-MM-DD end, empty string = no upper bound */
  toValue: string;
  onChange: (from: string, to: string) => void;
  sortable?: boolean;
  onSort?: () => void;
  sortIcon?: React.ReactNode;
}

/**
 * Excel-style column header filter for date ranges.
 * - Renders the column label with a small ▾ icon
 * - Click opens a popover with From / To date pickers + quick presets
 * - Active range shows a blue dot indicator
 */
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  label, fromValue, toValue, onChange, sortable, onSort, sortIcon,
}) => {
  const [open, setOpen] = useState(false);
  const [localFrom, setLocalFrom] = useState(fromValue);
  const [localTo, setLocalTo] = useState(toValue);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = fromValue !== '' || toValue !== '';

  useEffect(() => { setLocalFrom(fromValue); setLocalTo(toValue); }, [fromValue, toValue]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const apply = (from: string, to: string) => {
    onChange(from, to);
    setOpen(false);
  };

  const applyPreset = (preset: 'today' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisFY' | 'lastFY' | 'last7' | 'last30' | 'last90') => {
    const today = new Date();
    const yyyy = (d: Date) => d.toISOString().slice(0, 10);
    let from = '', to = '';
    if (preset === 'today') {
      from = to = yyyy(today);
    } else if (preset === 'thisWeek') {
      const day = today.getDay() || 7; // Sunday=0 → 7
      const monday = new Date(today); monday.setDate(today.getDate() - (day - 1));
      from = yyyy(monday); to = yyyy(today);
    } else if (preset === 'thisMonth') {
      from = yyyy(new Date(today.getFullYear(), today.getMonth(), 1));
      to = yyyy(today);
    } else if (preset === 'lastMonth') {
      from = yyyy(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      to = yyyy(new Date(today.getFullYear(), today.getMonth(), 0));
    } else if (preset === 'thisFY') {
      // Indian FY: Apr 1 - Mar 31
      const y = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      from = yyyy(new Date(y, 3, 1));
      to = yyyy(today);
    } else if (preset === 'lastFY') {
      const y = today.getMonth() >= 3 ? today.getFullYear() - 1 : today.getFullYear() - 2;
      from = yyyy(new Date(y, 3, 1));
      to = yyyy(new Date(y + 1, 2, 31));
    } else if (preset === 'last7') {
      const d = new Date(today); d.setDate(today.getDate() - 7);
      from = yyyy(d); to = yyyy(today);
    } else if (preset === 'last30') {
      const d = new Date(today); d.setDate(today.getDate() - 30);
      from = yyyy(d); to = yyyy(today);
    } else if (preset === 'last90') {
      const d = new Date(today); d.setDate(today.getDate() - 90);
      from = yyyy(d); to = yyyy(today);
    }
    apply(from, to);
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1">
        {sortable ? (
          <button
            className="font-semibold text-left flex items-center gap-0.5 hover:text-blue-700"
            onClick={onSort}
          >
            {label} {sortIcon}
          </button>
        ) : (
          <span className="font-semibold">{label}</span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          className={`shrink-0 w-4 h-4 flex items-center justify-center rounded text-[10px] transition-colors ${
            isActive
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
          title={isActive ? `${fromValue || '…'} → ${toValue || '…'}` : 'Filter by date range'}
        >
          ▾
        </button>
      </div>

      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-3"
          style={{ minWidth: 260 }}>
          <div className="space-y-2">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-0.5">From</label>
              <input type="date" className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300"
                value={localFrom} onChange={(e) => setLocalFrom(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-0.5">To</label>
              <input type="date" className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300"
                value={localTo} onChange={(e) => setLocalTo(e.target.value)} />
            </div>
          </div>

          <div className="border-t border-gray-100 mt-3 pt-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">Quick presets</div>
            <div className="grid grid-cols-3 gap-1 text-[11px]">
              <button className="px-2 py-1 rounded bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700" onClick={() => applyPreset('today')}>Today</button>
              <button className="px-2 py-1 rounded bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700" onClick={() => applyPreset('thisWeek')}>This Week</button>
              <button className="px-2 py-1 rounded bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700" onClick={() => applyPreset('thisMonth')}>This Month</button>
              <button className="px-2 py-1 rounded bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700" onClick={() => applyPreset('lastMonth')}>Last Month</button>
              <button className="px-2 py-1 rounded bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700" onClick={() => applyPreset('thisFY')}>This FY</button>
              <button className="px-2 py-1 rounded bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700" onClick={() => applyPreset('lastFY')}>Last FY</button>
              <button className="px-2 py-1 rounded bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700" onClick={() => applyPreset('last7')}>Last 7d</button>
              <button className="px-2 py-1 rounded bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700" onClick={() => applyPreset('last30')}>Last 30d</button>
              <button className="px-2 py-1 rounded bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700" onClick={() => applyPreset('last90')}>Last 90d</button>
            </div>
          </div>

          <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
            <button
              className="flex-1 text-xs bg-blue-600 text-white rounded py-1.5 font-medium hover:bg-blue-700"
              onClick={() => apply(localFrom, localTo)}
            >
              Apply
            </button>
            {isActive && (
              <button
                className="flex-1 text-xs text-red-600 border border-red-200 hover:bg-red-50 rounded py-1.5 font-medium"
                onClick={() => apply('', '')}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Helper: returns true if date d (Date | ISO string) falls within [from, to] inclusive (empty bounds = open). */
export function inDateRange(d: string | Date | null | undefined, from: string, to: string): boolean {
  if (!d) return !from && !to; // no date → only match when no filter
  const dy = typeof d === 'string' ? d.slice(0, 10) : d.toISOString().slice(0, 10);
  if (from && dy < from) return false;
  if (to && dy > to) return false;
  return true;
}

export default DateRangeFilter;
